// Login page — visually matches the desktop's `AuthForm` card (same rounded
// `tg-surface` card, same typographic scale, same input/button classes, same
// error-alert treatment). Posts to `/api/v1/auth/login` on the orchestrator.
import { useState } from "react";
import { ApiError, httpClient } from "../../client/http-client";
import { jwtStore } from "../../client/jwt-store";

const cardClassName =
	"tg-surface tg-text-primary w-full max-w-[560px] rounded-[18px] p-6 shadow-[var(--shadow-tg-elevated)]";
const inputClassName =
	"tg-input tg-text-primary h-12 w-full rounded-[16px] px-4";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-10 items-center justify-center rounded-xl px-4 text-[0.95rem] font-semibold disabled:cursor-wait disabled:opacity-60";

interface LoginPageProps {
	onAuthenticated: () => void;
	onForgotPassword: () => void;
}

const linkButtonClassName =
	"tg-text-secondary hover:tg-text-primary cursor-pointer text-[0.82rem] font-semibold underline-offset-4 hover:underline";

export const LoginPage = ({
	onAuthenticated,
	onForgotPassword,
}: LoginPageProps) => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async () => {
		setError(null);
		if (!email.trim() || !password) {
			setError("Enter your email and password to sign in.");
			return;
		}
		setBusy(true);
		try {
			const response = await httpClient.auth.login({
				email: email.trim(),
				password,
			});
			jwtStore.set({
				token: response.token,
				expiresAt: response.expiresAt,
				user: response.user,
			});
			onAuthenticated();
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else {
				setError(err instanceof Error ? err.message : String(err));
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className={cardClassName}>
			<div>
				<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
					Sign in to SignalLens
				</span>
				<h2 className="tg-text-primary max-w-[14ch] text-[clamp(1.95rem,2.8vw,2.8rem)] leading-[1.06] font-bold">
					Welcome back
				</h2>
				<p className="tg-text-muted mt-2.5 max-w-[46ch] text-[0.95rem] leading-7">
					Sign in with the email and password for your SignalLens account to
					reach your trading profiles.
				</p>
			</div>

			<form
				className="mt-5 grid gap-3.5"
				onSubmit={(event) => {
					event.preventDefault();
					void handleSubmit();
				}}
			>
				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						Email
					</span>
					<input
						className={inputClassName}
						autoFocus
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						placeholder="name@example.com"
						type="email"
						inputMode="email"
						autoComplete="email"
						disabled={busy}
					/>
				</label>

				<label className="grid gap-2">
					<div className="flex items-center justify-between gap-3">
						<span className="tg-text-primary text-[0.92rem] font-semibold">
							Password
						</span>
						<button
							type="button"
							onClick={onForgotPassword}
							className={linkButtonClassName}
							disabled={busy}
						>
							Forgot password?
						</button>
					</div>
					<input
						className={inputClassName}
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						placeholder="Your password"
						type="password"
						autoComplete="current-password"
						disabled={busy}
					/>
				</label>

				<button
					className={primaryButtonClassName}
					type="submit"
					disabled={busy}
				>
					{busy ? "Signing in..." : "Sign in"}
				</button>
			</form>

			{error ? (
				<div className="tg-alert-error mt-4 rounded-2xl px-4 py-3" role="alert">
					{error}
				</div>
			) : null}

			<p className="tg-text-muted mt-5 text-[0.82rem] leading-6">
				Need an account? Ask an administrator — new accounts are created
				from the User Management page inside SignalLens, not via public
				signup.
			</p>
		</section>
	);
};
